/**
 * Shared constants for the AWS IAM setup helpers (per-account, organization,
 * cross-account role). Kept out of the component files so fast-refresh stays
 * happy and the values have a single home — every snippet and the
 * "What access do I need?" guide render from these.
 */

// Read-only AWS-managed policies everything relies on. ReadOnlyAccess covers
// every Describe/List/Get the AWS scan and the WAFR evidence sweep call; the
// billing policy covers Cost Explorer (spend by service/region). Neither
// grants any write.
export const MANAGED_POLICY_ARNS = [
  'arn:aws:iam::aws:policy/ReadOnlyAccess',
  'arn:aws:iam::aws:policy/AWSBillingReadOnlyAccess',
]

// Name used for the per-account IAM user, the org cross-account role and the
// assume-role role. The org role name MUST match backend
// aws_import_collector.ORG_ASSUME_ROLE_NAME — the importer assumes
// `arn:aws:iam::<account>:role/<this>` in each member.
export const READONLY_NAME = 'ai-assessment-readonly'

// The ONLY writes the platform ever makes into a customer account: saving a
// finished WAFR into the customer's Well-Architected Tool (services/wafr_watool.py).
// Optional — without them the review completes in our platform and the
// consultant publishes to the WA Tool by hand. Mirrors the calls wafr_watool
// makes (the List* reads are already in ReadOnlyAccess; listed for clarity).
// TagResource covers the ownership tag CreateWorkload sets (Tags=…) — some
// services authorize create-time tags against it separately.
export const WA_TOOL_PUBLISH_ACTIONS = [
  'wellarchitected:CreateWorkload',
  'wellarchitected:UpdateAnswer',
  'wellarchitected:CreateMilestone',
  'wellarchitected:ListWorkloads',
  'wellarchitected:ListAnswers',
  'wellarchitected:ListMilestones',
  'wellarchitected:ListTagsForResource',
  'wellarchitected:TagResource',
]

export const WA_TOOL_PUBLISH_POLICY_NAME = 'wafr-wa-tool-publish'

/** The inline policy document for the optional WA Tool publish grant. */
export const WA_TOOL_PUBLISH_POLICY = JSON.stringify(
  {
    Version: '2012-10-17',
    Statement: [{ Effect: 'Allow', Action: WA_TOOL_PUBLISH_ACTIONS, Resource: '*' }],
  },
  null,
  2,
)

/** An optional, commented CLI step that adds the WA Tool publish grant to a
 *  user or role — appended to every helper's CLI/CloudShell snippet so a
 *  customer setting up for a WAFR can include it in the same paste. */
export function waToolCliStep(kind: 'user' | 'role', name: string, withComment = true): string {
  const flag = kind === 'user' ? '--user-name' : '--role-name'
  const comment = withComment
    ? `
# OPTIONAL — Well-Architected Review only: let AllCode save the finished review
# into your Well-Architected Tool (the only write we ever make). Skip otherwise.`
    : ''
  return `${comment}
aws iam put-${kind}-policy ${flag} ${name} \\
  --policy-name ${WA_TOOL_PUBLISH_POLICY_NAME} \\
  --policy-document '${JSON.stringify({ Version: '2012-10-17', Statement: [{ Effect: 'Allow', Action: WA_TOOL_PUBLISH_ACTIONS, Resource: '*' }] })}'`
}
